import { useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { getStatusError } from "@/utils/utils";
import {
	analiseConsultar,
	analiseConsultarTopProdutos,
	baixarExportExcel,
	consultarVitrineAgrupamento,
	consultarMetricas,
	gerarExportExcel,
	gerarPDFVitrine,
	getProdutoDistribuicaoCores,
	getProdutoDistribuicaoTable,
	recuperarOTBGradeProduto,
	recuperarOTBGradeResumida,
	recuperarResumoOTB,
	recoverMongoQuery,
	recoverQueries,
	recoverSQLQuery,
	getProdutoInfo,
} from "../control/compra-inteligente.spaceAPI";
import type {
	EAnaliseEstoqueRequestFieldKey,
	IDownloadExportExcelPayload,
	IGerarExportExcelPayload,
	IConsultarMetricasPayload,
	IInventoryAnalysisPayload,
	ITopProdutosPayload,
	IOTBAgrupamentoValor,
	IOTBGradeResumidaPayload,
	IProductDistributionColorsPayload,
	IProductDistributionPayload,
	IProductInfoPayload,
	IVitrineCursorPaginacao,
	IVitrineAgrupamentoPayload,
} from "../dtos/compra-inteligente.dto";
import {
	LIMITE_ITENS_PDF_VITRINE,
	normalizarFaixaPrecoValorVitrine,
	normalizarFiltrosVitrineAgrupamento,
} from "../control/utils/compraInteligenteUtils";
import { baixarBlob, montarNomeArquivo } from "@/utils/files";

type AgrupamentoExportacaoExcel = IGerarExportExcelPayload["agrupar_por"];
type IVitrineAgrupamentoEstruturaPayload = Omit<
	IVitrineAgrupamentoPayload,
	"cursor" | "modo" | "grupo_principal" | "sub_item"
>;
type IVitrineAgrupamentoItensPayload = Omit<IVitrineAgrupamentoPayload, "cursor" | "modo">;

export const keys_compraInteligente = {
	base: ["space"] as const,
	compraInteligente: () => [...keys_compraInteligente.base, "compra-inteligente"] as const,
	analiseEstoque: () => [...keys_compraInteligente.compraInteligente(), "analise-estoque"] as const,
	produto: () => [...keys_compraInteligente.analiseEstoque(), "produto"] as const,
	produtoInfo: (payload: IProductInfoPayload | null) =>
		[...keys_compraInteligente.produto(), "info", payload] as const,
	queries: () => [...keys_compraInteligente.analiseEstoque(), "queries"] as const,
	analise: (payload: IInventoryAnalysisPayload | null) =>
		[...keys_compraInteligente.analiseEstoque(), "analise", payload] as const,
	topProdutos: (payload: ITopProdutosPayload | null) =>
		[...keys_compraInteligente.analiseEstoque(), "top-produtos", payload] as const,
	produtoDistribuicao: (payload?: IProductDistributionPayload | null) =>
		[...keys_compraInteligente.produto(), "distribuicao", payload] as const,
	produtoDistribuicaoCores: (payload?: IProductDistributionColorsPayload | null) =>
		[...keys_compraInteligente.produto(), "distribuicao-cores", payload] as const,
	sqlQuery: (reportId: string | null) => [...keys_compraInteligente.queries(), "sql", reportId] as const,
	mongoQuery: (payload: IInventoryAnalysisPayload | null) =>
		[...keys_compraInteligente.queries(), "mongo", payload] as const,
	historicoQueries: () => [...keys_compraInteligente.queries(), "historico"] as const,
	metricas: (payload: IConsultarMetricasPayload | null) =>
		[...keys_compraInteligente.analiseEstoque(), "metricas", payload] as const,
	otbResumo: (
		idRelatorio: string | null,
		agrupamento: readonly EAnaliseEstoqueRequestFieldKey[],
		agrupamentoValor?: IOTBAgrupamentoValor | null,
	) =>
		[
			...keys_compraInteligente.compraInteligente(),
			"otb",
			"resumo",
			idRelatorio,
			agrupamento,
			agrupamentoValor ?? null,
		] as const,
	otbGradeResumida: (payload?: IOTBGradeResumidaPayload | null) =>
		[...keys_compraInteligente.compraInteligente(), "otb", "grade-resumida", payload] as const,
	otbGradeProduto: (payload?: IProductDistributionPayload | null) =>
		[...keys_compraInteligente.compraInteligente(), "otb", "grade-produto", payload] as const,
	vitrineAgrupamentoContexto: (
		payload?: Pick<
			IVitrineAgrupamentoPayload,
			"id_relatorio" | "organizacao" | "faixa_preco_valor" | "top_n"
		> | null,
	) =>
		[
			...keys_compraInteligente.analiseEstoque(),
			"vitrine",
			"contexto",
			payload?.id_relatorio ?? null,
			payload?.organizacao ?? "AGRUPAMENTO",
			payload?.faixa_preco_valor ?? null,
			payload?.top_n ?? null,
		] as const,
	vitrineAgrupamentoEstrutura: (payload?: IVitrineAgrupamentoEstruturaPayload | null) =>
		[
			...keys_compraInteligente.vitrineAgrupamentoContexto(payload),
			"estrutura",
			payload?.agrupamento ?? [],
			payload?.filtros ?? null,
		] as const,
	vitrineAgrupamentoItens: (payload?: IVitrineAgrupamentoItensPayload | null) =>
		[
			...keys_compraInteligente.vitrineAgrupamentoContexto(payload),
			"itens",
			payload?.agrupamento ?? [],
			payload?.grupo_principal ?? null,
			payload?.sub_item ?? null,
			payload?.filtros ?? null,
			payload?.limit ?? null,
		] as const,
};

function normalizarPayloadExcel(payload: IGerarExportExcelPayload): IGerarExportExcelPayload {
	const idRelatorio = String(payload.id_relatorio ?? "")
		.trim()
		.toUpperCase();

	if (!idRelatorio) throw new Error("Relatório inválido para exportação do Excel.");

	return {
		...payload,
		id_relatorio: idRelatorio,
	};
}

function normalizarPayloadDownloadExcel(payload: IDownloadExportExcelPayload): IDownloadExportExcelPayload {
	const idRelatorio = String(payload.id_relatorio ?? "")
		.trim()
		.toUpperCase();
	const idArquivo = String(payload.id_arquivo ?? "").trim();

	if (!idRelatorio) throw new Error("Relatório inválido para download do Excel.");
	if (!idArquivo) throw new Error("Arquivo inválido para download do Excel.");

	return {
		id_relatorio: idRelatorio,
		id_arquivo: idArquivo,
	};
}

function normalizarPayloadProdutoDistribuicaoOTB(
	payload?: IProductDistributionPayload | null,
): IProductDistributionPayload | null {
	const idRelatorio = String(payload?.id_relatorio ?? "")
		.trim()
		.toUpperCase();

	if (!payload || !idRelatorio) return null;

	return {
		...payload,
		id_relatorio: idRelatorio,
	};
}

function normalizarPayloadBaseVitrineAgrupamento(
	payload?: IVitrineAgrupamentoEstruturaPayload | null,
): IVitrineAgrupamentoEstruturaPayload | null {
	const idRelatorio = String(payload?.id_relatorio ?? "")
		.trim()
		.toUpperCase();

	if (!payload || !idRelatorio) return null;

	const organizacao = payload.organizacao ?? "AGRUPAMENTO";
	if (organizacao === "AGRUPAMENTO" && !payload.agrupamento.length) return null;
	if (organizacao === "FAIXA_PRECO" && (payload.agrupamento.length > 0 || payload.top_n?.agrupar_por !== "produto"))
		return null;

	const filtros = normalizarFiltrosVitrineAgrupamento(payload.filtros);
	const faixaPrecoValor = normalizarFaixaPrecoValorVitrine(payload.faixa_preco_valor);

	return {
		id_relatorio: idRelatorio,
		agrupamento: [...payload.agrupamento],
		...(payload.organizacao ? { organizacao: payload.organizacao } : {}),
		...(payload.top_n ? { top_n: payload.top_n } : {}),
		faixa_preco_valor: faixaPrecoValor,
		...(filtros ? { filtros } : {}),
	};
}

function normalizarPayloadItensVitrineAgrupamento(
	payload?: IVitrineAgrupamentoItensPayload | null,
): IVitrineAgrupamentoItensPayload | null {
	const base = normalizarPayloadBaseVitrineAgrupamento(payload);
	if (!base || !payload) return null;

	const limitRaw = Number(payload.limit ?? 50);
	const limitNormalizado = Number.isFinite(limitRaw) && limitRaw > 0 ? Math.floor(limitRaw) : 50;
	if (base.organizacao === "FAIXA_PRECO") {
		if (!base.filtros?.faixa_preco) return null;

		return {
			...base,
			filtros: { faixa_preco: base.filtros.faixa_preco },
			limit: limitNormalizado,
		};
	}

	if (!payload.grupo_principal) return null;

	const exigeSubItem = base.agrupamento.length > 1;
	if (exigeSubItem && !payload.sub_item) return null;

	return {
		...base,
		grupo_principal: {
			chave: payload.grupo_principal.chave,
			id: payload.grupo_principal.id,
		},
		...(payload.sub_item
			? {
					sub_item: {
						chave: payload.sub_item.chave,
						id: payload.sub_item.id,
					},
				}
			: {}),
		...(base.filtros ? { filtros: base.filtros } : {}),
		limit: limitNormalizado,
	};
}

function normalizarPayloadPDFVitrine(
	payload?: IVitrineAgrupamentoItensPayload | null,
): IVitrineAgrupamentoItensPayload | null {
	const base = normalizarPayloadBaseVitrineAgrupamento(payload);
	if (!base) return null;
	if (base.organizacao === "FAIXA_PRECO")
		return {
			...base,
			...(base.filtros?.faixa_preco ? { filtros: { faixa_preco: base.filtros.faixa_preco } } : {}),
			limit: LIMITE_ITENS_PDF_VITRINE,
		};

	return {
		...base, // já traz filtros normalizados (inclui faixa_preco)
		...(payload?.grupo_principal
			? { grupo_principal: { chave: payload.grupo_principal.chave, id: payload.grupo_principal.id } }
			: {}),
		...(payload?.sub_item ? { sub_item: { chave: payload.sub_item.chave, id: payload.sub_item.id } } : {}),
		limit: LIMITE_ITENS_PDF_VITRINE,
	};
}

// -------------------------------------------- ANALISE DE ESTOQUE - CONSULTAR ------------------------------------
function useConsultaAnalise<TPayload extends IInventoryAnalysisPayload, TRow>(
	payload: TPayload | null,
	enabled: boolean,
	queryKey: readonly unknown[],
	consultar: (payloadConsulta: TPayload) => Promise<TRow[]>,
) {
	const queryClient = useQueryClient();

	return useQuery({
		queryKey,
		queryFn: async () => {
			if (!payload) throw new Error("Requisição sem payload");
			const rows = await consultar(payload);

			if (payload.gerar_dados)
				void queryClient.invalidateQueries({ queryKey: keys_compraInteligente.historicoQueries() });

			return rows;
		},
		enabled: enabled && !!payload,
		staleTime: 5 * 60_000,
		retry: 0,
	});
}

export function useAnaliseConsultar(payload: IInventoryAnalysisPayload | null, enabled = true) {
	return useConsultaAnalise(payload, enabled, keys_compraInteligente.analise(payload), analiseConsultar);
}

export function useAnaliseConsultarTopProdutos(payload: ITopProdutosPayload | null, enabled = true) {
	return useConsultaAnalise(
		payload,
		enabled,
		keys_compraInteligente.topProdutos(payload),
		analiseConsultarTopProdutos,
	);
}

export function useConsultarMetricas(payload: IConsultarMetricasPayload | null, enabled = true) {
	return useQuery({
		queryKey: keys_compraInteligente.metricas(payload),
		// O `signal` aborta a consulta anterior quando o termo da busca muda, mantendo só a última.
		queryFn: ({ signal }) => {
			if (!payload) throw new Error("Requisição sem payload para métricas");
			return consultarMetricas(payload, signal);
		},
		enabled: enabled && !!payload,
		staleTime: 5 * 60_000,
		retry: 0,
	});
}

export function useGetProdutoInfo(payload: IProductInfoPayload | null, enabled = true) {
	return useQuery({
		queryKey: keys_compraInteligente.produtoInfo(payload),
		queryFn: () => {
			if (!payload) throw new Error("Requisição sem payload para informações do produto");
			return getProdutoInfo(payload);
		},
		enabled: enabled && !!payload,
		staleTime: 5 * 60_000,
		retry: 0,
	});
}

// --------------------------------------------------- PLANEJAMENTO OTB --------------------------------------------
export function useResumoOTB(
	idRelatorio: string | null | undefined,
	agrupamento: readonly EAnaliseEstoqueRequestFieldKey[],
	enabled = true,
	agrupamentoValor?: IOTBAgrupamentoValor | null,
) {
	const idRelatorioNormalizado = idRelatorio?.trim() || null;
	const agrupamentoValorNormalizado =
		agrupamentoValor && Object.keys(agrupamentoValor).length ? agrupamentoValor : null;

	return useQuery({
		queryKey: keys_compraInteligente.otbResumo(idRelatorioNormalizado, agrupamento, agrupamentoValorNormalizado),
		queryFn: async ({ signal }) => {
			if (!idRelatorioNormalizado) throw new Error("Relatório inválido para recuperar OTB.");
			if (!agrupamento.length) throw new Error("Agrupamento inválido para recuperar OTB.");

			let resultado = await recuperarResumoOTB(
				idRelatorioNormalizado,
				agrupamento,
				agrupamentoValorNormalizado,
				signal,
			);

			while (resultado.status !== "done" && resultado.status !== "error") {
				await new Promise<void>((resolve, reject) => {
					const timer = setTimeout(resolve, 5000);
					signal.addEventListener("abort", () => {
						clearTimeout(timer);
						reject(new DOMException("Query cancelada", "AbortError"));
					});
				});

				resultado = await recuperarResumoOTB(
					idRelatorioNormalizado,
					agrupamento,
					agrupamentoValorNormalizado,
					signal,
				);
			}

			return resultado;
		},
		enabled: enabled && !!idRelatorioNormalizado && agrupamento.length > 0,
		staleTime: 5 * 60_000,
		retry: 0,
	});
}

export function useOTBGradeResumida(payload?: IOTBGradeResumidaPayload | null, enabled = true) {
	const payloadNormalizado = payload?.id_relatorio?.trim()
		? {
				...payload,
				id_relatorio: payload.id_relatorio.trim().toUpperCase(),
			}
		: null;

	return useQuery({
		queryKey: keys_compraInteligente.otbGradeResumida(payloadNormalizado),
		queryFn: async ({ signal }) => {
			if (!payloadNormalizado) throw new Error("Requisição sem payload para grade resumida OTB.");
			if (!payloadNormalizado.agrupamento.length)
				throw new Error("Agrupamento inválido para recuperar grade resumida OTB.");

			let resultado = await recuperarOTBGradeResumida(payloadNormalizado, signal);

			while (resultado.status !== "done" && resultado.status !== "error") {
				await new Promise<void>((resolve, reject) => {
					const timer = setTimeout(resolve, 5000);
					signal.addEventListener("abort", () => {
						clearTimeout(timer);
						reject(new DOMException("Query cancelada", "AbortError"));
					});
				});

				resultado = await recuperarOTBGradeResumida(payloadNormalizado, signal);
			}

			return resultado;
		},
		enabled: enabled && !!payloadNormalizado && payloadNormalizado.agrupamento.length > 0,
		staleTime: 5 * 60_000,
		retry: 0,
	});
}

export function useOTBGradeProduto(payload?: IProductDistributionPayload | null, enabled = true) {
	const payloadNormalizado = normalizarPayloadProdutoDistribuicaoOTB(payload);

	return useQuery({
		queryKey: keys_compraInteligente.otbGradeProduto(payloadNormalizado),
		queryFn: async ({ signal }) => {
			if (!payloadNormalizado) throw new Error("Requisição sem payload para grade do produto OTB.");

			let resultado = await recuperarOTBGradeProduto(payloadNormalizado, signal);

			while (resultado.status !== "done" && resultado.status !== "error") {
				await new Promise<void>((resolve, reject) => {
					const timer = setTimeout(resolve, 5000);
					signal.addEventListener("abort", () => {
						clearTimeout(timer);
						reject(new DOMException("Query cancelada", "AbortError"));
					});
				});

				resultado = await recuperarOTBGradeProduto(payloadNormalizado, signal);
			}

			return resultado;
		},
		enabled: enabled && !!payloadNormalizado,
		staleTime: 5 * 60_000,
		retry: 0,
	});
}

// -------------------------------------------------- PRODUTO DISTRIBUIÇÃO --------------------------------------------
export function useProdutoDistribuicaoTable(payload?: IProductDistributionPayload | null, enabled = true) {
	return useQuery({
		queryKey: keys_compraInteligente.produtoDistribuicao(payload),
		queryFn: () => {
			if (!payload) throw new Error("Requisição sem payload de distribuição");
			return getProdutoDistribuicaoTable(payload);
		},
		enabled: enabled && !!payload,
		staleTime: 5 * 60_000,
		retry: 0,
	});
}

export function useProdutoDistribuicaoCores(payload?: IProductDistributionColorsPayload | null, enabled = true) {
	return useQuery({
		queryKey: keys_compraInteligente.produtoDistribuicaoCores(payload),
		queryFn: () => {
			if (!payload) throw new Error("Requisição sem payload de cores do produto");
			return getProdutoDistribuicaoCores(payload);
		},
		enabled: enabled && !!payload,
		staleTime: 5 * 60_000,
		retry: 0,
	});
}

// -------------------------------------------------VITRINE AGRUPAMENTO --------------------------------------------
export function useVitrineAgrupamentoEstrutura(payload?: IVitrineAgrupamentoEstruturaPayload | null, enabled = true) {
	const payloadNormalizado = normalizarPayloadBaseVitrineAgrupamento(payload);

	return useQuery({
		queryKey: keys_compraInteligente.vitrineAgrupamentoEstrutura(payloadNormalizado),
		queryFn: ({ signal }) => {
			if (!payloadNormalizado) throw new Error("Requisição sem payload para estrutura da vitrine.");

			return consultarVitrineAgrupamento(
				{
					...payloadNormalizado,
					modo: "ESTRUTURA",
				},
				signal,
			);
		},
		enabled: enabled && !!payloadNormalizado,
		staleTime: 5 * 60_000,
		retry: 0,
	});
}

export function useVitrineAgrupamentoItensInfinita(payload?: IVitrineAgrupamentoItensPayload | null, enabled = true) {
	const payloadNormalizado = normalizarPayloadItensVitrineAgrupamento(payload);

	return useInfiniteQuery({
		queryKey: keys_compraInteligente.vitrineAgrupamentoItens(payloadNormalizado),
		queryFn: ({ pageParam, signal }) => {
			if (!payloadNormalizado) throw new Error("Requisição sem payload para itens da vitrine agrupada.");

			return consultarVitrineAgrupamento(
				{
					...payloadNormalizado,
					modo: "ITENS",
					cursor: pageParam as IVitrineCursorPaginacao | null,
				},
				signal,
			);
		},
		enabled: enabled && !!payloadNormalizado,
		initialPageParam: null,
		getNextPageParam: (lastPage) => (lastPage.has_next ? (lastPage.cursor_proximo ?? undefined) : undefined),
		staleTime: 5 * 60_000,
		retry: 0,
	});
}

// -------------------------------------------------- QUERIES RECUPERADAS ---------------------------------------------
export function useInventoryRecoverSQLQuery(reportId: string | null | undefined, enabled = true) {
	const normalizedReportId = reportId?.trim() || null;

	return useQuery({
		queryKey: keys_compraInteligente.sqlQuery(normalizedReportId),
		queryFn: () => {
			if (!normalizedReportId) throw new Error("Relatório inválido para consulta SQL");
			return recoverSQLQuery(normalizedReportId);
		},
		enabled: enabled && !!normalizedReportId,
		staleTime: 5 * 60_000,
		retry: 0,
	});
}

export function useRecoverMongoQuery(payload: IInventoryAnalysisPayload | null, enabled = true) {
	return useQuery({
		queryKey: keys_compraInteligente.mongoQuery(payload),
		queryFn: () => {
			if (!payload) throw new Error("Requisição sem payload para recuperar consulta MongoDB");
			return recoverMongoQuery(payload);
		},
		enabled: enabled && !!payload,
		staleTime: 5 * 60_000,
		retry: 0,
	});
}

export function useRecoverQueries(enabled = true) {
	return useQuery({
		queryKey: keys_compraInteligente.historicoQueries(),
		queryFn: () => recoverQueries(),
		enabled,
		staleTime: 5 * 60_000,
		retry: 0,
	});
}

// -------------------------------------------------- EXPORT EXCEL ---------------------------------------------
export function useDownloadExportExcel() {
	const [agrupamentosExportando, setAgrupamentosExportando] = useState<ReadonlySet<AgrupamentoExportacaoExcel>>(
		() => new Set(),
	);

	const marcarAgrupamentoExportando = (agruparPor: AgrupamentoExportacaoExcel) => {
		setAgrupamentosExportando((prev) => {
			if (prev.has(agruparPor)) return prev;

			const next = new Set(prev);
			next.add(agruparPor);
			return next;
		});
	};

	const desmarcarAgrupamentoExportando = (agruparPor: AgrupamentoExportacaoExcel) => {
		setAgrupamentosExportando((prev) => {
			if (!prev.has(agruparPor)) return prev;

			const next = new Set(prev);
			next.delete(agruparPor);
			return next;
		});
	};

	const mutacaoGerarExportacaoExcel = useMutation({
		mutationFn: (payload: IGerarExportExcelPayload) => gerarExportExcel(normalizarPayloadExcel(payload)),
		onMutate: (payload) => {
			marcarAgrupamentoExportando(payload.agrupar_por);
		},
		onSuccess: async (response, variables) => {
			if (response.status === "already_processing") {
				enqueueSnackbar("Já existe arquivo deste tipo em processamento para este relatório.", {
					variant: "warning",
					autoHideDuration: 7000,
				});
				return;
			}

			if (response.status === "already_exists") {
				enqueueSnackbar("Arquivo já existente, baixando o arquivo...", {
					variant: "success",
					autoHideDuration: 7000,
				});

				const payloadDownload = normalizarPayloadDownloadExcel({
					id_relatorio: variables.id_relatorio,
					id_arquivo: response.id_arquivo,
				});
				const arquivoExcel = await baixarExportExcel(payloadDownload);
				if (arquivoExcel.size <= 0) throw new Error("Arquivo Excel retornado vazio.");

				return baixarBlob(
					arquivoExcel,
					montarNomeArquivo(
						`${payloadDownload.id_relatorio}-${payloadDownload.id_arquivo}`,
						"xlsx",
						"compra-inteligente",
					),
				);
			}

			enqueueSnackbar(
				"Foi solicitado o download do seu arquivo, ficará disponivel em alguns minutos em suas notificações...",
				{ variant: "info", autoHideDuration: 7000 },
			);
		},
		onError: (error) => {
			if (getStatusError(error) !== undefined) return; // HTTP já exibido pelo queryClient
			const message = error instanceof Error ? error.message : "Não foi possível exportar o Excel.";
			enqueueSnackbar(message, { variant: "error", autoHideDuration: 7000 });
		},
		onSettled: (_data, _error, variables) => {
			desmarcarAgrupamentoExportando(variables.agrupar_por);
		},
	});

	const mutacaoBaixarExportacaoExcel = useMutation({
		mutationFn: async (payload: IDownloadExportExcelPayload) => {
			const payloadNormalizado = normalizarPayloadDownloadExcel(payload);
			const arquivoExcel = await baixarExportExcel(payloadNormalizado);
			if (arquivoExcel.size <= 0) throw new Error("Arquivo Excel retornado vazio.");

			baixarBlob(
				arquivoExcel,
				montarNomeArquivo(
					`${payloadNormalizado.id_relatorio}-${payloadNormalizado.id_arquivo}`,
					"xlsx",
					"compra-inteligente",
				),
			);
			return payloadNormalizado;
		},
		onError: (error) => {
			if (getStatusError(error) !== undefined) return; // HTTP já exibido pelo queryClient
			const message = error instanceof Error ? error.message : "Não foi possível baixar o Excel.";
			enqueueSnackbar(message, { variant: "error", autoHideDuration: 7000 });
		},
	});

	const agrupamentosEmAndamento = Array.from(agrupamentosExportando);
	const agrupamentoExportando = agrupamentosEmAndamento.length === 1 ? (agrupamentosEmAndamento[0] ?? null) : null;

	return {
		exportarExcel: mutacaoGerarExportacaoExcel.mutate,
		baixarExcel: mutacaoBaixarExportacaoExcel.mutate,
		exportandoExcel: agrupamentosExportando.size > 0,
		exportandoSku: agrupamentosExportando.has("sku"),
		exportandoProduto: agrupamentosExportando.has("produto"),
		baixandoExcel: mutacaoBaixarExportacaoExcel.isPending,
		agrupamentoExportando,
		erro: mutacaoGerarExportacaoExcel.error ?? mutacaoBaixarExportacaoExcel.error,
	};
}

// -------------------------------------------------- PDF VITRINE ----------------------------------------------
export function useGerarPDFVitrine() {
	const mutation = useMutation({
		mutationFn: async (payload: IVitrineAgrupamentoItensPayload) => {
			const payloadNormalizado = normalizarPayloadPDFVitrine(payload);
			if (!payloadNormalizado) throw new Error("Payload inválido para gerar o PDF da vitrine.");

			const arquivoPDF = await gerarPDFVitrine(payloadNormalizado);
			if (arquivoPDF.size <= 0) throw new Error("PDF da vitrine retornado vazio.");

			baixarBlob(arquivoPDF, montarNomeArquivo(payloadNormalizado.id_relatorio, "pdf", "vitrine"));
			return payloadNormalizado;
		},
		onError: (error) => {
			if (getStatusError(error) !== undefined) return; // HTTP já exibido pelo queryClient
			const message = error instanceof Error ? error.message : "Não foi possível gerar o PDF da vitrine.";
			enqueueSnackbar(message, { variant: "error", autoHideDuration: 7000 });
		},
	});

	return {
		gerarPDF: mutation.mutate,
		gerandoPDF: mutation.isPending,
		erro: mutation.error,
	};
}
